import { useEffect, useState, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { useAssignedDigitalTrays } from '../AssignedDigitalTrays.context'

import { BetterIDTrayScrew } from 'views/SPDLayout/SPD/SPD.types'
import { TrayScrew, HardwareDispositionStatus } from './TrayVisualization.types'
import useTrayMapLogic from 'views/SPDLayout/SPD/TrayMap/TrayMap.logic'
import { useSPD } from 'views/SPDLayout/SPD/SPD.context'
import {
  OnClickActions,
  PlateKey,
} from 'views/SPDLayout/SPD/TrayMap/TrayMap.types'
import { UsageOtherItem, UsagePlate } from '../AssignedDigitalTrays.types'
import { extractSubTrayJSONData } from 'lib/utils/extractSubTrayJSONData'
import { useEditSubTrayItemMutation } from 'lib/apollo/hooks'
import { getProductMatchingFunction } from 'utils/getProductMatchingFunction'
import useGrayScaleLogic from 'components/organisms/GrayScale/GrayScale.logic'
import { enqueueSnackbar } from 'notistack'

const useTrayVisualizationLogic = () => {
  const navigate = useNavigate()
  const { surgeryId } = useParams()

  const { editSubTrayItem } = useEditSubTrayItemMutation()

  const {
    trayDetails,
    spdScrews,
    usageScrews,
    setUsageScrews,
    usagePlates,
    setUsagePlates,
    selectedSubTray,
    setSelectedSubTray,
    usageOtherItems,
    setUsageOtherItems,
  } = useAssignedDigitalTrays()
  const {
    trayPlates,
    setTrayPlates,
    trayScrews,
    setTrayScrews,
    selectedScrew,
    setSelectedScrew,
    selectedPlate,
    setSelectedPlate,
    selectedScrewIndices,
    setSelectedScrewIndices,
    setParLevel,
    otherItems,
    setOtherItems,
    customImplants,
    setCustomImplants,
  } = useSPD()
  const { getSelectedJSONFile, getInitialPlateCount, handlePlateCountChange } =
    useTrayMapLogic({ trayType: trayDetails?.trayType })
  const { getSelectedTrayScrews } = useGrayScaleLogic({
    trayType: trayDetails?.trayType || 'stryker screw caddy',
  })

  const [trayHeight, setTrayHeight] = useState<number | string>('70vh')
  const [modalOpen, setModalOpen] = useState<boolean>(false)
  const [selectedStatus, setSelectedStatus] =
    useState<HardwareDispositionStatus>('implanted')
  const [confirmChangesModalOpen, setConfirmChangesModalOpen] = useState(false)
  const [isConfirmScrewRemovalModalOpen, setIsConfirmScrewRemovalModalOpen] =
    useState(false)
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false)
  const [isInEditMode, setIsInEditMode] = useState<boolean>(false)
  const [selectedScrews, setSelectedScrews] = useState<BetterIDTrayScrew[]>([])
  const [otherItemsModalOpen, setOtherItemsModalOpen] = useState<boolean>(false)

  const matchScrewsToProductData = getProductMatchingFunction(
    trayDetails?.trayType
  )

  const trayType = useMemo(() => {
    if (selectedSubTray) {
      const details = extractSubTrayJSONData(selectedSubTray)
      return details?.trayType
    }
    return undefined
  }, [selectedSubTray])

  const mapContent = useMemo(
    () => ({
      screws: trayDetails?.screws || [],
      plates: trayDetails?.plates || [],
    }),
    [trayDetails]
  )

  const hasStartedDocumentingUsage =
    usageScrews.length > 0 ||
    usagePlates.length > 0 ||
    usageOtherItems.length > 0
  const shouldPopulateTrayContents = trayDetails && !hasStartedDocumentingUsage

  useEffect(() => {
    const handleResize = () => {
      const headerHeight =
        document.getElementById('medgeo-app-header')?.offsetHeight || 0
      const statusBarHeight =
        document.getElementById('procedure-status-bar')?.offsetHeight || 0
      const footerHeight =
        document.getElementById('asset-list-footer')?.offsetHeight || 0
      const remainingHeight =
        window.innerHeight - headerHeight - footerHeight - statusBarHeight
      setTrayHeight(remainingHeight > 0 ? remainingHeight : '70vh')
    }

    window.addEventListener('resize', handleResize)
    handleResize()

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  useEffect(() => {
    if (shouldPopulateTrayContents) {
      setTrayScrews(trayDetails.screws || [])
      setOtherItems(trayDetails.otherItems || [])
      setCustomImplants(trayDetails.customImplants || [])
      setParLevel(trayDetails.parLevel || 1)
      if (
        usagePlates.length === 0 ||
        (usagePlates.length > 0 && trayPlates.length === 0)
      ) {
        setTrayPlates(trayDetails.plates || [])
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldPopulateTrayContents])

  useEffect(() => {
    const selectedScrews = getSelectedTrayScrews(selectedScrewIndices)
    setSelectedScrews(selectedScrews)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedScrewIndices])

  const isScrewUsed = (label: string, row: number, column: number) => {
    return usageScrews.some(
      (screw: any) =>
        screw.label === label && screw.row === row && screw.x === column
    )
  }

  const isScrewUsedAndWasted = (label: string, row: number, column: number) => {
    return usageScrews.some(
      (screw: any) =>
        screw.label === label &&
        screw.row === row &&
        screw.x === column &&
        screw.wasted
    )
  }

  const isScrewLoadedFromSPD = (label: string, row: number, column: number) => {
    return spdScrews?.some(
      (screw: BetterIDTrayScrew) =>
        screw.label === label && screw.row === row && screw.x === column
    )
  }
  const trayData = getSelectedJSONFile(trayType)
  const screwAreas = trayData.regions[0].areas

  const findRemovedScrew = (label: string, row: number, column: number) => {
    if (!surgeryId || !selectedSubTray) return undefined

    const subTrayDetails = extractSubTrayJSONData(selectedSubTray)
    if (!subTrayDetails?.removedByNurse?.[surgeryId]) return undefined

    return subTrayDetails.removedByNurse[surgeryId].find(
      (screw: BetterIDTrayScrew) =>
        screw.label === label && screw.row === row && screw.x === column
    )
  }

  const handleScrewSelection = (
    label: string,
    rowIndex: number,
    column: number,
    x: number
  ) => {
    if (!isInEditMode) {
      return
    }
    const newSelectedScrew = {
      label: label,
      row: rowIndex,
      column: column,
      x,
    }
    setSelectedScrew(newSelectedScrew)

    // Check if screw is in usageScrews or removedByNurse
    if (
      isScrewUsed(
        newSelectedScrew.label,
        newSelectedScrew.row,
        newSelectedScrew.x
      ) ||
      isScrewUsedAndWasted(
        newSelectedScrew.label,
        newSelectedScrew.row,
        newSelectedScrew.x
      ) ||
      findRemovedScrew(
        newSelectedScrew.label,
        newSelectedScrew.row,
        newSelectedScrew.x
      )
    ) {
      // remove screw from list
      setIsConfirmScrewRemovalModalOpen(true)
    } else {
      setModalOpen(true)
    }
  }

  const findScrew = (label: string, row: number, column: number) => {
    return spdScrews?.find(
      (screw: BetterIDTrayScrew) =>
        screw.label === label && screw.row === row && screw.x === column
    )
  }

  const updateRemovedByNurse = (screws: BetterIDTrayScrew[]) => {
    if (!surgeryId || !selectedSubTray) return

    const subTrayDetails = extractSubTrayJSONData(selectedSubTray)

    if (!subTrayDetails) return

    const newRemovedByNurse = {
      ...subTrayDetails.removedByNurse,
      [surgeryId]: [
        ...(subTrayDetails?.removedByNurse?.[surgeryId] || []),
        ...screws,
      ],
    }

    // Also update the screws in the subTrayDetails to match current trayScrews
    // This ensures trayDetails will have the updated screws when it's refreshed
    const updatedScrews = trayScrews.filter(
      (trayScrew) =>
        !screws.some(
          (screw) =>
            trayScrew.label === screw.label &&
            trayScrew.row === screw.row &&
            trayScrew.x === screw.x
        )
    )

    const newSubTrayDetails = {
      ...subTrayDetails,
      removedByNurse: newRemovedByNurse,
      screws: updatedScrews,
    }
    const newSubTrayDetailsString = JSON.stringify(newSubTrayDetails)

    setSelectedSubTray((prevSelectedSubTray) =>
      prevSelectedSubTray
        ? {
            ...prevSelectedSubTray,
            productDetails: newSubTrayDetailsString,
          }
        : null
    )
  }

  const removeScrew = (screw: BetterIDTrayScrew) => {
    if (!screw || !trayScrews) return

    const updatedTrayScrews = trayScrews.filter(
      (trayScrew) =>
        !(
          trayScrew.label === screw.label &&
          trayScrew.row === screw.row &&
          trayScrew.x === screw.x
        )
    )

    setTrayScrews(updatedTrayScrews)
  }

  const handleStatusChange = () => {
    if (selectedScrews.length > 0) {
      const screwsFromSPD = selectedScrews
        .map((screw) => {
          return (
            findScrew(screw.label, screw.row, screw.x) ||
            findRemovedScrew(screw.label, screw.row, screw.x)
          )
        })
        .filter(Boolean) as BetterIDTrayScrew[]

      let newScrews: BetterIDTrayScrew[] = []

      // Process all screws at once
      screwsFromSPD.forEach((screw) => {
        const productData = matchScrewsToProductData(screw.label, screw.column)
        const newScrew = {
          ...screw,
          deviceId: screw.deviceId || productData.deviceID,
          deviceDescription: screw.deviceDescription || productData.title,
          expirationDate: null,
          wasted: selectedStatus === 'wasted',
        }
        newScrews.push(newScrew)
      })

      // Update usageScrews once with all screws if not "not present"
      if (selectedStatus !== 'not present') {
        setUsageScrews((prevUsageScrews) => [...prevUsageScrews, ...newScrews])
      }

      // Remove all screws at once
      setTrayScrews((prevTrayScrews) =>
        prevTrayScrews.filter(
          (trayScrew) =>
            !screwsFromSPD.some(
              (spd) =>
                spd.label === trayScrew.label &&
                spd.row === trayScrew.row &&
                spd.x === trayScrew.x
            )
        )
      )

      if (selectedStatus === 'not present') {
        updateRemovedByNurse(newScrews)
      }
    } else {
      if (
        !selectedScrew?.label ||
        selectedScrew?.row === undefined ||
        selectedScrew?.x === undefined
      ) {
        return
      }

      // First check if the screw is in SPD
      let screwToProcess = findScrew(
        selectedScrew.label,
        selectedScrew.row,
        selectedScrew.x
      )

      // If not in SPD, check if it's in removedByNurse
      if (!screwToProcess) {
        screwToProcess = findRemovedScrew(
          selectedScrew.label,
          selectedScrew.row,
          selectedScrew.x
        )
      }

      if (screwToProcess) {
        const productData = matchScrewsToProductData(
          selectedScrew.label,
          selectedScrew.column
        )

        const newScrew = {
          ...screwToProcess,
          deviceId: screwToProcess.deviceId || productData?.deviceID,
          deviceDescription:
            screwToProcess.deviceDescription || productData?.title,
          expirationDate: null,
          wasted: selectedStatus === 'wasted',
        }

        if (selectedStatus === 'not present') {
          updateRemovedByNurse([newScrew])
        } else {
          setUsageScrews((prevUsageScrews) => {
            const newUsageScrews = [...prevUsageScrews, newScrew]
            return newUsageScrews
          })
        }

        // Only remove from trayScrews if it's actually in SPD
        if (
          findScrew(selectedScrew.label, selectedScrew.row, selectedScrew.x)
        ) {
          removeScrew(screwToProcess)
        }
      }
    }
    setModalOpen(false)
    setSelectedScrew(null)
    setSelectedScrewIndices([])
    setSelectedStatus('implanted')
  }

  const handleConfirm = async () => {
    if (!selectedSubTray || !surgeryId) return

    const currentProductDetails = extractSubTrayJSONData(selectedSubTray)
    const nurseUsageHistory = currentProductDetails?.nurseUsageHistory || {}

    const updatedNurseUsageHistory = {
      ...nurseUsageHistory,
      [surgeryId]: {
        screws: [
          ...(nurseUsageHistory[surgeryId]?.screws || []),
          ...usageScrews,
        ],
        plates: [
          ...(nurseUsageHistory[surgeryId]?.plates || []),
          ...usagePlates,
        ],
        customImplants: [
          ...(nurseUsageHistory[surgeryId]?.customImplants || []),
          ...customImplants,
        ],
        otherItems: [
          ...(nurseUsageHistory[surgeryId]?.otherItems || []),
          ...otherItems,
        ],
      },
    }

    const newProductDetails = JSON.stringify({
      ...currentProductDetails,
      screws: trayScrews,
      plates: trayPlates,
      nurseUsageHistory: updatedNurseUsageHistory,
      otherItems,
      customImplants,
    })

    const editResult = await editSubTrayItem({
      id: selectedSubTray.id,
      productDetails: newProductDetails,
    })

    if (editResult) {
      setConfirmChangesModalOpen(false)
      navigate('../tray-products')
    } else {
      enqueueSnackbar('Failed to save changes', {
        variant: 'error',
      })
    }
  }

  const handleOtherItemsModalOpen = () => {
    setOtherItemsModalOpen(true)
  }

  const handleOtherItemsModalClose = () => {
    setOtherItemsModalOpen(false)
  }

  const handleAddOtherItemToUsage = (item: UsageOtherItem) => {
    // Add the item to usage
    setUsageOtherItems((prev) => [...prev, item])

    // Remove the item from otherItems based on the quantity being added
    setOtherItems((prevItems) => {
      const itemToUpdate = prevItems.find(
        (prevItem) => prevItem.device_id === item.deviceId
      )

      if (!itemToUpdate || itemToUpdate.count <= 0) {
        return prevItems
      }

      const quantityToRemove = item.count
      const newCount = Math.max(0, itemToUpdate.count - quantityToRemove)
      const isRemoved = newCount <= 0

      const updatedItems = prevItems
        .map((prevItem) =>
          prevItem.device_id === item.deviceId
            ? {
                ...prevItem,
                count: newCount,
                removed: isRemoved,
              }
            : prevItem
        )
        .filter((item) => !item.removed)

      return updatedItems
    })
  }

  const handleSaveChangesCancellation = () => {
    setConfirmChangesModalOpen(false)
    navigate('../tray-products')
  }

  const handleReloadScrew = () => {
    const newScrews = trayScrews.filter(
      (screw: TrayScrew) =>
        !(
          screw.label === selectedScrew?.label &&
          screw.row === selectedScrew?.row &&
          screw.x === selectedScrew?.x
        )
    )
    const newUsageScrews = usageScrews?.filter(
      (screw: BetterIDTrayScrew) =>
        !(
          screw.label === selectedScrew?.label &&
          screw.row === selectedScrew?.row &&
          screw.x === selectedScrew?.x
        )
    )
    setSelectedScrew(null)
    setUsageScrews(newUsageScrews)
    setTrayScrews(newScrews)
    setIsConfirmScrewRemovalModalOpen(false)
  }

  const handleHelpModalClose = () => {
    setIsHelpModalOpen(false)
  }

  const handleReloadScrewModalClose = () => {
    setIsConfirmScrewRemovalModalOpen(false)
    setSelectedScrew(null)
  }

  const handleConfirmChangesModalClose = () => {
    setConfirmChangesModalOpen(false)
  }

  const handleRemoveScrewModalClose = () => {
    setSelectedScrew(null)
    setModalOpen(false)
  }

  const updatePlateCount = (plateName: string) => {
    const trayPlateMatch = trayPlates.find(
      (plate) => plate.plateName === plateName
    )

    if (trayPlateMatch) {
      const newUsagePlate: UsagePlate = {
        deviceId: trayPlateMatch.deviceId,
        plateName: trayPlateMatch.plateName,
        wasted: selectedStatus === 'wasted',
      }

      setUsagePlates([...usagePlates, newUsagePlate])
    }
  }

  const handlePlateUsage = (plateName: PlateKey, newCount: number) => {
    const plateMatch = trayPlates.find(
      (trayPlate) => trayPlate.plateName === plateName
    )
    if (plateMatch && plateMatch.plateCount > 0) {
      setSelectedPlate(plateMatch)
      setModalOpen(true)
    }
  }

  const handlePlateStatusChange = () => {
    if (selectedPlate) {
      if (selectedStatus !== 'not present') {
        updatePlateCount(selectedPlate.plateName)
      }

      const newPlateCountInSPD: number = selectedPlate.plateCount - 1

      handlePlateCountChange(selectedPlate.plateName, newPlateCountInSPD)

      setModalOpen(false)
      setSelectedPlate(null)
    }
  }

  const onClickActions: OnClickActions = {
    screw: handleScrewSelection,
    plates: handlePlateUsage,
  }

  return {
    selectedScrew,
    setSelectedScrew,
    trayHeight,
    setTrayHeight,
    modalOpen,
    setModalOpen,
    selectedStatus,
    setSelectedStatus,
    confirmChangesModalOpen,
    setConfirmChangesModalOpen,
    isConfirmScrewRemovalModalOpen,
    setIsConfirmScrewRemovalModalOpen,
    isHelpModalOpen,
    setIsHelpModalOpen,
    trayScrews,
    setTrayScrews,
    isScrewUsed,
    isScrewUsedAndWasted,
    isScrewLoadedFromSPD,
    screwAreas,
    handleScrewSelection,
    handleStatusChange,
    handleConfirm,
    handleSaveChangesCancellation,
    handleReloadScrew,
    handleReloadScrewModalClose,
    handleHelpModalClose,
    handleConfirmChangesModalClose,
    handleRemoveScrewModalClose,
    findScrew,
    isInEditMode,
    setIsInEditMode,
    trayType,
    getInitialPlateCount,
    handlePlateCountChange,
    handlePlateUsage,
    trayDetails,
    mapContent,
    onClickActions,
    selectedPlate,
    updatePlateCount,
    handlePlateStatusChange,
    setSelectedPlate,
    selectedScrews,
    otherItems,
    setOtherItems,
    otherItemsModalOpen,
    setOtherItemsModalOpen,
    handleOtherItemsModalOpen,
    handleOtherItemsModalClose,
    handleAddOtherItemToUsage,
    usageOtherItems,
  }
}

export default useTrayVisualizationLogic
